import { logger } from '@luna/logger';
import { uuid } from '@luna/utils';
import { getEnv } from '@luna/env';

import {
    generateCodeVerifier,
    generateNonce,
    getCodeChallengeFromCodeVerifier,
} from '../pkceUtils';
import { setAuthData } from '../authDataService';
import type { AuthConfig } from '../types';
import { setBacklinkPathToStorage } from '../backlinkPathService';
import { submitForm } from '../submitForm';
import { saveLocalhostCountry } from '../localhostRedirectService';
import { storeCsrfToken } from './CsrfTokenService';

export function isOIDCInUrl(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('oidc') === 'true';
}

const getCodeChallengeAndCodeVerifier = async (): Promise<{
    codeVerifier: string;
    codeChallenge: string;
}> => {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await getCodeChallengeFromCodeVerifier(codeVerifier);

    return { codeVerifier, codeChallenge };
};

const getFedLoginBaseUrl = (environment: AuthConfig): URL => {
    if (__DEV__ && isOIDCInUrl() && environment.auth.fedLoginForDevUrl) {
        return new URL(environment.auth.fedLoginForDevUrl);
    }

    return new URL(environment.auth.fedLoginUrl);
};

const getFedLoginUrl = (
    environment: AuthConfig,
    {
        codeChallenge,
        nonce,
        state,
        idTokenHint,
    }: { codeChallenge: string; nonce: string; state?: string; idTokenHint?: string },
): string => {
    const fedLoginUrl = getFedLoginBaseUrl(environment);
    fedLoginUrl.searchParams.append('response_type', environment.auth.responseType);
    fedLoginUrl.searchParams.append('client_id', environment.clientId);
    fedLoginUrl.searchParams.append('scope', environment.auth.scope || 'openid');
    fedLoginUrl.searchParams.append('code_challenge_method', environment.auth.codeChallengeMethod);
    fedLoginUrl.searchParams.append('code_challenge', codeChallenge);
    fedLoginUrl.searchParams.append('redirect_uri', environment.auth.redirectUri);
    fedLoginUrl.searchParams.append('nonce', nonce);
    if (idTokenHint) {
        fedLoginUrl.searchParams.append('id_token_hint', idTokenHint);
        fedLoginUrl.searchParams.append('prompt', 'none');
    }
    if (state) {
        fedLoginUrl.searchParams.append('state', state);
    }
    return fedLoginUrl.href;
};

const getFormData = (
    environment: AuthConfig,
    {
        codeChallenge,
        nonce,
        state,
        idTokenHint,
    }: { codeChallenge: string; nonce: string; state?: string; idTokenHint?: string },
): Record<string, string> => {
    const data: Record<string, string> = {
        client_id: environment.clientId,
        code_challenge: codeChallenge,
        code_challenge_method: environment.auth.codeChallengeMethod,
        nonce,
        redirect_uri: environment.auth.redirectUri,
        response_type: environment.auth.responseType,
        response_mode: 'fragment',
        scope: environment.auth.scope || 'openid',
    };

    if (state) {
        data.state = state;
    }
    if (idTokenHint) {
        data.id_token_hint = idTokenHint;
        data.prompt = 'none';
    }

    return data;
};
function getParsedJsonOrAnEmptyObject(s = ''): Record<string, unknown> {
    try {
        return JSON.parse(s);
    } catch {
        // pass
    }

    return {};
}

export const redirectToOidcLoginPage = async (
    environment: AuthConfig,
    path: string,
    state?: string,
    loginMethod: 'GET' | 'POST' = 'GET',
    idTokenHint?: string,
): Promise<void> => {
    setBacklinkPathToStorage(path);
    saveLocalhostCountry();

    let stateToSend = state;

    if (getEnv().oidcCsrf) {
        const stateObject = getParsedJsonOrAnEmptyObject(state);
        const csrfToken = uuid();
        storeCsrfToken(csrfToken);
        stateObject.csrfToken = csrfToken;
        stateToSend = JSON.stringify(stateObject);
    }

    const { codeChallenge, codeVerifier } = await getCodeChallengeAndCodeVerifier();
    const nonce = generateNonce();

    setAuthData({
        codeVerifier,
        nonce,
    });

    if (loginMethod === 'GET') {
        logger.info('redirectToOidcLoginPage: GET method');
        window.location.href = getFedLoginUrl(environment, {
            codeChallenge,
            nonce,
            state: stateToSend,
            idTokenHint,
        });
    } else {
        logger.info('redirectToOidcLoginPage: POST method');
        submitForm(getFedLoginBaseUrl(environment).toString(), {
            method: 'POST',
            body: getFormData(environment, {
                codeChallenge,
                nonce,
                state: stateToSend,
                idTokenHint,
            }),
        });
    }
};
